import { Injectable } from '@angular/core';
import { UserManager, UserManagerSettings, User } from 'oidc-client';
import { BehaviorSubject, concat, filter, from, map, mergeMap, Observable, take, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IUser } from '../common/IUser.interface';
import { ApplicationPaths } from './api-authorization.constants';
import { buildLanguage, languageFor } from '../common/formatting-locale';

export type IAuthenticationResult =
  SuccessAuthenticationResult |
  FailureAuthenticationResult |
  RedirectAuthenticationResult;

export interface SuccessAuthenticationResult {
  status: AuthenticationResultStatus.Success;
  state: any;
}

export interface FailureAuthenticationResult {
  status: AuthenticationResultStatus.Fail;
  /**
   * #178: what went wrong, as a code. `message` is whatever the OIDC library threw — English
   * at best and a raw exception at worst — and it used to be rendered to the user verbatim,
   * including through a query string. The code is what the UI translates; the message stays
   * for the console.
   */
  code: AuthFailureCode;
  message: string;
}

/** The ways signing in or out can fail here. Client-side, so deliberately not backend ErrorCodes. */
export type AuthFailureCode =
  | 'signInFailed'
  | 'signInCancelled'
  | 'signInCallbackFailed'
  | 'signOutFailed';

export interface RedirectAuthenticationResult {
  status: AuthenticationResultStatus.Redirect;
}

export enum AuthenticationResultStatus {
  Success,
  Redirect,
  Fail
}

@Injectable({
  providedIn: 'root'
})
export class AuthorizeService {

  constructor() {
  }
  readonly sesstionStorageRetailerMode = 'RetailerMode';

  private popUpDisabled = true;
  private userManager = new UserManager(this.getUserManagerSettings());
  private userSubject: BehaviorSubject<IUser | null> = new BehaviorSubject<IUser | null>(null);
  private settings: UserManagerSettings = this.getUserManagerSettings();
  public $retailerMode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public async completeSignIn(url: string): Promise<IAuthenticationResult> {
    try {
      await this.ensureUserManagerInitialized();
      const user = await this.userManager.signinCallback(url);
      this.userSubject.next(user && user.profile);
      return this.success(user && user.state);
    } catch (error) {
      console.log('There was an error signing in: ', error);
      return this.error('signInCallbackFailed', error);
    }
  }

  public getUser(): Observable<IUser | null> {
    return concat(
      this.userSubject.pipe(take(1), filter(u => !!u)),
      this.getUserFromStorage().pipe(
        filter(u => !!u),
        tap(u => this.userSubject.next(u))
      ),
      this.userSubject.asObservable());
  }

  public isAuthenticated(): Observable<boolean> {
    return this.getUser().pipe(
      map(u => !!u),
    );
  }

  public async signIn(state: any): Promise<IAuthenticationResult> {
    await this.ensureUserManagerInitialized();
    let user: User | null = null;
    try {
      user = await this.userManager.signinSilent(this.createArguments());
      this.userSubject.next(user.profile);
      return this.success(state);
    } catch (silentError) {
      // User might not be authenticated, fallback to popup authentication
      console.log('Silent authentication error: ', silentError);

      try {
        if (this.popUpDisabled) {
          throw new Error('Popup disabled. Change \'authorize.service.ts:AuthorizeService.popupDisabled\' to false to enable it.');
        }
        user = await this.userManager.signinPopup(this.createArguments());
        this.userSubject.next(user.profile);
        return this.success(state);
      } catch (popupError: any) {
        if (popupError.message === 'Popup window closed') {
          // The user explicitly cancelled the login action by closing an opened popup.
          return this.error('signInCancelled', 'The user closed the window.');
        } else if (!this.popUpDisabled) {
          console.log('Popup authentication error: ', popupError);
        }

        // PopUps might be blocked by the user, fallback to redirect
        try {
          await this.userManager.signinRedirect(this.createArguments(state));
          return this.redirect();
        } catch (redirectError: any) {
          console.log('Redirect authentication error: ', redirectError);
          return this.error('signInFailed', redirectError);
        }
      }
    }
  }

  public async signOut(state: any): Promise<IAuthenticationResult> {
    try {
      if (this.popUpDisabled) {
        throw new Error('Popup disabled. Change \'authorize.service.ts:AuthorizeService.popupDisabled\' to false to enable it.');
      }

      await this.ensureUserManagerInitialized();
      await this.userManager.signoutPopup(this.createArguments());
      this.userSubject.next(null);
      return this.success(state);
    } catch (popupSignOutError) {
      console.log('Popup signout error: ', popupSignOutError);
      try {
        await this.userManager.signoutRedirect(this.createArguments(state));
        return this.redirect();
      } catch (redirectSignOutError: any) {
        console.log('Redirect signout error: ', popupSignOutError);
        return this.error('signOutFailed', redirectSignOutError);
      }
    }
  }

  public async signOutLocal(): Promise<void> {
    await this.ensureUserManagerInitialized();
    await this.userManager.removeUser();
    this.userSubject.next(null);
    sessionStorage.removeItem(this.sesstionStorageRetailerMode);
  }

  public async completeSignOut(url: string): Promise<IAuthenticationResult> {
    await this.ensureUserManagerInitialized();
    try {
      const response = await this.userManager.signoutCallback(url);
      this.userSubject.next(null);
      sessionStorage.removeItem(this.sesstionStorageRetailerMode);
      return this.success(response && response.state);
    } catch (error: any) {
      sessionStorage.removeItem(this.sesstionStorageRetailerMode);
      console.log(`There was an error trying to log out '${error}'.`);
      return this.error('signOutFailed', error);
    }
  }

  public getAccessToken(): Observable<string> {
    return from(this.ensureUserManagerInitialized())
      .pipe(mergeMap(() => from(this.userManager.getUser())),
        map(user => user && user.access_token));
  }

  public setRetailerMode(retailerMode: boolean) {
    sessionStorage.setItem(this.sesstionStorageRetailerMode, retailerMode ? '1' : '0');
    this.$retailerMode.next(retailerMode);
  }

  private redirect(): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Redirect };
  }

  private createArguments(state?: any): any {
    return { useReplaceToNavigate: true, data: state };
  }

  private getUserFromStorage(): Observable<IUser | null> {
    return from(this.ensureUserManagerInitialized())
      .pipe(
        mergeMap(() => this.userManager.getUser()),
        map(u => u && u.profile));
  }

  private async ensureUserManagerInitialized(): Promise<void> {
    if (this.userManager !== undefined) {
      return;
    }

    this.userManager = new UserManager(this.settings);

    this.userManager.events.addUserSignedOut(async () => {
      await this.userManager.removeUser();
      this.userSubject.next(null);
    });
  }

  /**
   * #221 / backend #705: the IDP's change-password page for the signed-in user. The password
   * changes on the IDP and nowhere else — the page needs the IDP's own cookie and the current
   * password, neither of which this app holds, so the link is a full-page navigation and
   * never an XHR. `returnUrl` is where the page's "Weiter" and "Abbrechen" lead: the IDP
   * accepts it when its origin matches a redirect or post-logout URI registered for this
   * client (any page of this app qualifies, no new URI to register) and falls back to the
   * portal homepage otherwise. `ui_locales` follows the bundle, for the reason given below.
   */
  public changePasswordUrl(returnUrl: string): string {
    return (
      `${environment.identityServerUrl}${ApplicationPaths.IdentityChangePasswordPath}` +
      `?client_id=${encodeURIComponent(environment.clientId)}` +
      `&returnUrl=${encodeURIComponent(returnUrl)}` +
      `&ui_locales=${languageFor(buildLanguage())}`
    );
  }

  private getUserManagerSettings(): UserManagerSettings {
    return {
      authority: environment.identityServerUrl,
      client_id: environment.clientId,
      redirect_uri: environment.home_uri + "/" + ApplicationPaths.LoginCallback,
      response_type: environment.responseType,
      scope: environment.scope,
      post_logout_redirect_uri: environment.home_uri + "/" + ApplicationPaths.LogOutCallback,
      // backend #682: the IDP honours ui_locales ahead of the browser's own language. Without
      // it the sign-in and registration pages follow whatever the browser asks for, which is
      // routinely not the language of the app the user started from — and since the mail slice
      // it decides more than how a page looks: the registration, child-user and password-reset
      // mails are rendered by those pages, so an app in English without this sends a German
      // mail. Read from the bundle rather than from a stored preference, for the same reason
      // the Accept-Language header is (see LanguageInterceptor); `$localize.locale` is what
      // LOCALE_ID itself is derived from, and taking it directly keeps this out of DI, which
      // the UserManager is built outside of.
      ui_locales: languageFor(buildLanguage()),
      filterProtocolClaims: true,
      loadUserInfo: true
    };
  }

  private error(code: AuthFailureCode, message: unknown): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Fail, code, message: String(message) };
  }

  private success(state: any): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Success, state };
  }
}
